import React, { useState } from "react"
import {
  Card,
  CardBody,
  Table,
  Button,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import { Link } from "react-router-dom"
import { isEmpty } from "lodash"
import SAlert from "components/Alert"
import ActionColumns from "components/Common/GeneralTable/ActionColumns"
import DeleteModal from "components/Common/DeleteModal"
import CustomHeaderTitle from "components/Common/CustomHeaderTitle"

const ReceiptsSubmitedList = ({
  data = [],
  loading = false,
  title = "Draft Items",
  onDeleteDraft, // Callback for deleting single draft
  onDeleteManyDrafts, // Callback for deleting multiple drafts
  onEditDraft, // Optional: callback for editing drafts
}) => {
  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedDraft, setSelectedDraft] = useState(null)
  const [removeLoading, setRemoveLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])

  // Format date for display
  const formatDate = dateString => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  // Format currency
  const formatCurrency = amount => {
    if (!amount) return "$0.00"
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  // Handle single draft deletion
  const handleDeleteDraft = draft => {
    setSelectedDraft(draft)
    setDeleteModal(true)
  }

  // Confirm single draft deletion
  const confirmDeleteDraft = () => {
    if (!selectedDraft || !onDeleteDraft) {
      setDeleteModal(false)
      return
    }

    onDeleteDraft(selectedDraft.id)
    setDeleteModal(false)
    setSelectedDraft(null)
  }

  // Handle multiple draft deletion
  const handleDeleteManyDrafts = () => {
    if (selectedIds.length === 0) {
      SAlert({ text: "No drafts selected", status: "info" })
      return
    }
    onDeleteManyDrafts(selectedIds)
    setSelectedIds([])
  }

  // Handle checkbox selection
  const handleCheckboxChange = (draftId, isChecked) => {
    if (isChecked) {
      setSelectedIds(prev => [...prev, draftId])
    } else {
      setSelectedIds(prev => prev.filter(id => id !== draftId))
    }
  }

  // Handle select all checkbox
  const handleSelectAll = isChecked => {
    if (isChecked) {
      setSelectedIds(data.map(draft => draft.id))
    } else {
      setSelectedIds([])
    }
  }

  // Calculate totals
  const calculateTotals = () => {
    if (isEmpty(data)) return { totalItems: 0, totalValue: 0, totalQuantity: 0 }

    return data.reduce(
      (acc, draft) => ({
        totalItems: acc.totalItems + 1,
        totalValue: acc.totalValue + (parseFloat(draft.total_value) || 0),
        totalQuantity: acc.totalQuantity + (draft.in_qty_per_ip || 0),
      }),
      { totalItems: 0, totalValue: 0, totalQuantity: 0 }
    )
  }

  const totals = calculateTotals()

  if (loading) {
    return (
      <Card className="mt-4">
        <CardBody className="text-center">
          <Spinner color="primary" />
          <div className="mt-2">Loading drafts...</div>
        </CardBody>
      </Card>
    )
  }

  if (isEmpty(data)) {
    return (
      <Card className="mt-4">
        <CardBody className="text-center text-muted">
          <i className="bx bx-package display-4" />
          <h5 className="mt-2">No Draft Items</h5>
          <p>Start by adding items using the form above.</p>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      <Card className="mt-4">
        <CardBody>
          <CustomHeaderTitle
            loading={false}
            title={title}
            lgColumn={17}
            orderingList={false}
            searchBox={false}
            rightBtn={false}
            // deleteBtn={true}
            onAllDeleteClicked={handleDeleteManyDrafts}
            // selectedIds={selectedIds}
          />

          {/* Summary Stats */}
          <div className="d-flex gap-3 text-muted mb-3">
            <small>
              <strong>Total Value:</strong> {totals.totalValue.toLocaleString()}
            </small>
            <small>
              <strong>Total Quantity:</strong>{" "}
              {totals.totalQuantity.toLocaleString()}
            </small>
          </div>

          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  {/* <th>
                    <input
                      type="checkbox"
                      checked={
                        selectedIds.length === data.length && data.length > 0
                      }
                      onChange={e => handleSelectAll(e.target.checked)}
                    />
                  </th> */}
                  <th>#</th>
                  <th>IP- Item Description </th>
                  <th>Batch Number</th>
                  <th>Expire Date</th>
                  <th>Quantity (IP UOM)</th>
                  <th>Unit Price</th>
                  <th>Total Value</th>
                  <th>Submission Date</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((draft, index) => (
                  <tr key={draft.id || index}>
                    {/* <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(draft.id)}
                        onChange={e =>
                          handleCheckboxChange(draft.id, e.target.checked)
                        }
                      />
                    </td> */}
                    <td>{index + 1}</td>
                    <td>
                      <div>
                        <strong>
                          {draft.item?.ip_item_description ||
                            draft.item_name ||
                            "N/A"}
                        </strong>
                      </div>
                    </td>
                    <td>
                      <Badge color="light" className="text-dark">
                        {draft.batch_number || "N/A"}
                      </Badge>
                    </td>
                    <td>{formatDate(draft.expire_date)}</td>
                    <td>
                      <strong>
                        {draft.in_qty_per_ip?.toLocaleString() || "0"}
                      </strong>
                      {draft.lost_quantity > 0 && (
                        <div className="text-danger small">
                          Lost: {draft.lost_quantity}
                        </div>
                      )}
                    </td>
                    <td>
                      {draft.unit_price_per_unfpa && draft.unit_price_per_unfpa}
                    </td>
                    <td>
                      <strong className="text-success">
                        {draft.total_value && draft.total_value}
                      </strong>
                    </td>
                    <td>{formatDate(draft.date)}</td>
                    <td>
                      <Badge
                        color={draft.status === "draft" ? "warning" : "success"}
                        className="text-capitalize"
                      >
                        {draft.status || "draft"}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <ActionColumns
                          data={draft}
                          editRoute={`/edit-provider-receipts-draft/${draft.id}`}
                          enableEdit={true}
                          enableDelete={true}
                          onClickDelete={handleDeleteDraft}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {/* Footer Summary */}
          <div className="mt-3 p-3 bg-light rounded">
            <div className="row text-center">
              <div className="col">
                <h6 className="mb-0">{totals.totalItems}</h6>
                <small className="text-muted">Total Receipts</small>
              </div>
              <div className="col">
                <h6 className="mb-0">
                  {totals.totalQuantity.toLocaleString()}
                </h6>
                <small className="text-muted">Total Quantity</small>
              </div>
              <div className="col">
                <h6 className="mb-0 text-success">
                  {totals.totalValue.toLocaleString()}
                </h6>
                <small className="text-muted">Total Value</small>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        show={deleteModal}
        onDeleteClick={confirmDeleteDraft}
        onCloseClick={() => setDeleteModal(false)}
      />
    </>
  )
}

export default ReceiptsSubmitedList
